import React, { useMemo, useState } from 'react';
import { Check, Globe, Link2, Plus, MessageCircle, Settings } from 'lucide-react';
import { SwitcherChevronsIcon } from '@/components/icons/SwitcherChevronsIcon';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { SocialAvatar } from '@/components/social/SocialAvatar';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { type Workspace } from '@/hooks/useWorkspaces';
import { useBusinessProfile } from '@/hooks/useBusinessProfile';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { useWorkspaceCustomImage } from '@/hooks/useWorkspaceCustomImage';
import { cn } from '@/lib/utils';
import { WorkspaceAvatar, workspaceAccountId } from '@/components/workspaces/WorkspaceAvatar';
import { ACCOUNT_BUSINESS_ACCOUNTS_URL } from './SidebarAccountFooter';
import { CreateWorkspaceDialog } from './CreateWorkspaceDialog';
import { canConnectWhatsAppToWorkspace } from '@/lib/workspaces/workspaceDelete';
import {
  canCreateWorkspace,
  canOpenWorkspaceSettings,
  canUseManageWorkspacesPage,
  MANAGE_WORKSPACES_URL,
  WORKSPACE_SETTINGS_URL,
} from '@/lib/workspaces/manageWorkspaces';
import { useAuth } from '@/components/AuthProvider';
import { useSidebar } from '@/components/ui/sidebar';
import { useSwitchWorkspace } from '@/hooks/useSwitchWorkspace';

export const BUSINESS_ACCOUNTS_URL = ACCOUNT_BUSINESS_ACCOUNTS_URL;
const BUSINESS_ACCOUNTS_GATE_URL = '/home/business-accounts';
export const INTEGRATIONS_URL = '/home/integrations';

function accountPhone(phone: string | undefined | null): string | null {
  const trimmed = (phone || '').trim();
  return trimmed || null;
}

interface SidebarWorkspaceSwitcherProps {
  collapsed: boolean;
  canOpen: (url: string, featureKey: string | null) => boolean;
  onNavigate: (url: string) => void;
}

function WorkspaceIdentity({
  name,
  phone,
  isDefault,
}: {
  name: string;
  phone?: string | null;
  isDefault?: boolean;
}) {
  const phoneLabel = accountPhone(phone);
  return (
    <div className="min-w-0 flex-1 text-left">
      <div className="flex items-center gap-1">
        <div className="min-w-0 truncate text-[14px] font-medium leading-5 text-[#101828]" title={name}>{name}</div>
        {isDefault ? (
          <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-[3px] text-[9px] font-bold uppercase leading-none tracking-wider text-amber-700">
            Default
          </span>
        ) : null}
      </div>
      {phoneLabel ? (
        <div className="truncate text-[12px] leading-4 text-[#667085]">{phoneLabel}</div>
      ) : null}
    </div>
  );
}

function WorkspaceRowBody({ workspace, active }: {
  workspace: Workspace;
  active: boolean;
}) {
  return (
    <>
      <WorkspaceAvatar
        accountId={workspaceAccountId(workspace)}
        workspaceId={workspace.id}
        name={workspace.name}
        sizeClass="h-8 w-8"
      />
      <WorkspaceIdentity
        name={workspace.name}
        phone={workspace.whatsapp?.phone_number}
        isDefault={workspace.is_default}
      />
      {active ? <Check className="h-4 w-4 shrink-0 text-brand" /> : null}
    </>
  );
}

function rowClass(active: boolean) {
  return cn(
    'group flex w-full items-center gap-1 rounded-xl px-2 py-2',
    active ? 'bg-brand-soft' : 'hover:bg-[#F9FAFB]',
  );
}

function RowIconAction({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[#98A2B3] opacity-0 outline-none transition-opacity hover:bg-[#EAECF0] hover:text-[#475467] focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-[#12B76A]/30 group-hover:opacity-100',
        className,
      )}
    >
      {children}
    </button>
  );
}

function WorkspaceMenu({
  workspaces,
  selectedWorkspaceId,
  isPending,
  showEmpty,
  canAddWorkspace,
  canCreateWorkspace: showCreateWorkspace,
  canManageWorkspaces,
  canOpenWorkspaceSettings: showWorkspaceSettings,
  canOpenIntegrations,
  onSelect,
  onAdd,
  onCreate,
  onManage,
  onWorkspaceSettings,
  onRowSettings,
  onIntegrations,
}: {
  workspaces: Workspace[];
  selectedWorkspaceId: string | null;
  isPending: boolean;
  showEmpty: boolean;
  canAddWorkspace: boolean;
  canCreateWorkspace: boolean;
  canManageWorkspaces: boolean;
  canOpenWorkspaceSettings: boolean;
  canOpenIntegrations: boolean;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onCreate: () => void;
  onManage: () => void;
  onWorkspaceSettings: () => void;
  onRowSettings: (workspace: Workspace) => void;
  onIntegrations: () => void;
}) {
  return (
    <>
      <div data-testid="sidebar-workspaces-section">
        <div className="px-1.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#98A2B3]">
          Workspaces
        </div>
        <div className="max-h-[220px] overflow-y-auto" data-testid="sidebar-workspaces-list">
          {isPending ? (
            <div className="px-1.5 py-2 text-[13px] text-[#667085]">Loading workspaces…</div>
          ) : showEmpty ? (
            <div className="px-1.5 py-2 text-[13px] text-[#667085]" data-testid="sidebar-workspaces-empty">
              {showCreateWorkspace
                ? 'No workspaces yet'
                : 'No workspaces assigned to you yet'}
            </div>
          ) : (
            workspaces.map((workspace) => {
              const active = workspace.id === selectedWorkspaceId;
              return (
                <div key={workspace.id} className={rowClass(active)}>
                  <button
                    type="button"
                    onClick={() => onSelect(workspace.id)}
                    className="flex min-h-9 min-w-0 flex-1 items-center gap-2.5 text-left"
                  >
                    <WorkspaceRowBody workspace={workspace} active={active} />
                  </button>
                  {showWorkspaceSettings ? (
                    <RowIconAction
                      label={`Settings for ${workspace.name}`}
                      onClick={() => onRowSettings(workspace)}
                    >
                      <Settings className="h-3.5 w-3.5" />
                    </RowIconAction>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </div>
      {showCreateWorkspace ? (
        <button
          type="button"
          data-testid="sidebar-create-workspace"
          onClick={onCreate}
          className="mt-1 flex h-9 w-full items-center gap-2 rounded-lg px-2 font-medium text-brand hover:bg-[#F9FAFB]"
        >
          <Plus className="h-4 w-4" />
          New workspace
        </button>
      ) : null}
      {canManageWorkspaces ? (
        <button
          type="button"
          data-testid="sidebar-manage-workspaces"
          onClick={onManage}
          className="flex h-9 w-full items-center gap-2 rounded-lg px-2 font-medium text-[#475467] hover:bg-[#F9FAFB]"
        >
          <Settings className="h-4 w-4" />
          Manage Workspace
        </button>
      ) : null}
      {showWorkspaceSettings ? (
        <button
          type="button"
          data-testid="sidebar-workspace-settings"
          onClick={onWorkspaceSettings}
          className="flex h-9 w-full items-center gap-2 rounded-lg px-2 font-medium text-[#475467] hover:bg-[#F9FAFB]"
        >
          <Globe className="h-4 w-4" />
          Workspace settings
        </button>
      ) : null}
      {canOpenIntegrations ? (
        <button
          type="button"
          data-testid="sidebar-integrations"
          onClick={onIntegrations}
          className="flex h-9 w-full items-center gap-2 rounded-lg px-2 font-medium text-[#475467] hover:bg-[#F9FAFB]"
        >
          <Link2 className="h-4 w-4" />
          Integrations
        </button>
      ) : null}
    </>
  );
}

export function SidebarWorkspaceSwitcher({
  collapsed,
  canOpen,
  onNavigate,
}: SidebarWorkspaceSwitcherProps) {
  const { workspaces, selectedWorkspaceId, selectedWorkspace, isLoading } =
    useWorkspaceContext();
  const switchWorkspace = useSwitchWorkspace();
  const { isMobile, setOpenMobile } = useSidebar();
  const { user } = useAuth();
  const { data: entitlements } = useOrganizationEntitlements();
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const list = workspaces ?? [];
  const isPending = isLoading && !workspaces;
  const showEmpty = !isPending && list.length === 0;
  const selected = selectedWorkspace;

  const canAddWorkspace = useMemo(() => {
    if (!canOpen(BUSINESS_ACCOUNTS_GATE_URL, 'business_accounts')) return false;
    if (!entitlements?.entitlements?.limits) return false;

    const limit = entitlements.entitlements.limits.business_accounts;
    if (limit === -1) return true;

    const connectedCount = list.filter((w) => w.business_account_id).length;
    return connectedCount < (limit || 0);
  }, [canOpen, entitlements, list]);

  const canConnectHere = canAddWorkspace && canConnectWhatsAppToWorkspace(selected);

  const closeAndSelect = (id: string) => {
    setOpen(false);
    if (id !== selectedWorkspaceId && isMobile) setOpenMobile(false);
    switchWorkspace(id);
  };

  const closeAndAdd = () => {
    setOpen(false);
    onNavigate(BUSINESS_ACCOUNTS_URL);
  };

  const closeAndCreate = () => {
    setOpen(false);
    setCreateOpen(true);
  };

  const closeAndManage = () => {
    setOpen(false);
    onNavigate(MANAGE_WORKSPACES_URL);
  };

  const closeAndWorkspaceSettings = () => {
    setOpen(false);
    onNavigate(WORKSPACE_SETTINGS_URL);
  };

  const closeAndRowSettings = (workspace: Workspace) => {
    setOpen(false);
    if (isMobile) setOpenMobile(false);
    switchWorkspace(workspace.id, { to: WORKSPACE_SETTINGS_URL });
  };

  const closeAndIntegrations = () => {
    setOpen(false);
    onNavigate(INTEGRATIONS_URL);
  };

  const createDialog = (
    <CreateWorkspaceDialog
      open={createOpen}
      onOpenChange={setCreateOpen}
      onCreated={(id) => switchWorkspace(id)}
    />
  );

  const menu = (
    <WorkspaceMenu
      workspaces={list}
      selectedWorkspaceId={selectedWorkspaceId}
      isPending={isPending}
      showEmpty={showEmpty}
      canAddWorkspace={canConnectHere}
      canCreateWorkspace={canCreateWorkspace(user)}
      canManageWorkspaces={canUseManageWorkspacesPage(user)}
      canOpenWorkspaceSettings={
        canOpenWorkspaceSettings(user) && canOpen(WORKSPACE_SETTINGS_URL, null)
      }
      canOpenIntegrations={canOpen(INTEGRATIONS_URL, 'integrations')}
      onSelect={closeAndSelect}
      onAdd={closeAndAdd}
      onCreate={closeAndCreate}
      onManage={closeAndManage}
      onWorkspaceSettings={closeAndWorkspaceSettings}
      onRowSettings={closeAndRowSettings}
      onIntegrations={closeAndIntegrations}
    />
  );

  const hoverCard = (
    trigger: React.ReactNode,
    side: 'right' | 'bottom',
    wrapClassName = 'w-full min-w-0',
    triggerClassName = 'w-full',
  ) => (
    <div className={wrapClassName}>
      <HoverCard
        open={open}
        onOpenChange={setOpen}
        openDelay={80}
        closeDelay={220}
      >
        <HoverCardTrigger asChild className={triggerClassName}>{trigger}</HoverCardTrigger>
        <HoverCardContent
          side={side}
          align="start"
          sideOffset={8}
          collisionPadding={
            side === 'right'
              ? { top: 72, right: 8, bottom: 8, left: 8 }
              : 8
          }
          className="w-[280px] rounded-2xl border border-[#EAECF0] bg-white p-2 text-[14px] shadow-[0_12px_32px_rgba(16,24,40,0.12)] z-[80]"
        >
          {menu}
        </HoverCardContent>
      </HoverCard>
    </div>
  );

  if (collapsed) {
    return (
      <>
      <div
        className="flex w-full min-w-0 justify-center"
        data-testid="sidebar-workspace-switcher"
      >
        {hoverCard(
          <button
            type="button"
            data-tour="business-account-selector"
            aria-label={selected?.name ? `Workspace: ${selected.name}` : 'Workspaces'}
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full outline-none hover:opacity-90"
          >
            <WorkspaceAvatar
              accountId={workspaceAccountId(selected)}
              workspaceId={selected?.id}
              name={selected?.name}
              sizeClass="h-8 w-8"
              loading="eager"
            />
          </button>,
          'right',
          'flex w-full min-w-0 justify-center',
          'inline-flex shrink-0',
        )}
      </div>
      {createDialog}
      </>
    );
  }

  const triggerLabel = isPending
    ? 'Loading…'
    : selected?.name || (showEmpty ? 'No workspace' : 'Select workspace');

  return (
    <>
    <div
      data-testid="sidebar-workspace-switcher"
      className="shrink-0"
    >
      {hoverCard(
        <button
          type="button"
          data-tour="business-account-selector"
          aria-label={selected?.name ? `Workspace: ${selected.name}` : 'Workspaces'}
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="flex w-full min-w-0 items-center gap-2 rounded-lg bg-white px-2 py-2 text-left outline-none transition-colors shadow-[0_0_0_1px_#EAECF0] hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-[#12B76A]/30"
        >
          {isPending ? (
            <div className="h-5 w-5 shrink-0 rounded-md bg-[#F2F4F7]" aria-hidden />
          ) : (
            <WorkspaceAvatar
              accountId={workspaceAccountId(selected)}
              workspaceId={selected?.id}
              name={selected?.name}
              sizeClass="h-5 w-5"
              loading="eager"
            />
          )}
          <div className="min-w-0 flex-1 truncate text-[14.5px] font-medium text-[#101828]">
            {triggerLabel}
          </div>
          <SwitcherChevronsIcon className="ml-auto h-4 w-4 shrink-0 text-[#98A2B3]" />
        </button>,
        'bottom',
      )}
    </div>
    {createDialog}
    </>
  );
}
